import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { guestApi } from '../api';
import { PublicFacility } from '../api_types';
import { Spinner } from '../shared/Spinner';
import './styles/GuestPage.css';

export const GuestPage: React.FC = () => {
  const [facilities, setFacilities] = useState<PublicFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const data = await guestApi.getPublicFacilities();
        setFacilities(data);
        console.log(data);
      } catch (err) {
        setError('Failed to load facilities. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFacilities();
  }, []);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);
  const formatPrice = (facility: PublicFacility) => {
    const { weekendRate, weekdayPeakRate, weekdayOffPeakRate } = facility.publicPricing;
    
    // All rates are in cents, so we divide by 100 to get dollars
    const rates = [
      weekendRate / 100,
      weekdayPeakRate / 100,
      weekdayOffPeakRate / 100
    ];

    const minPrice = Math.min(...rates);
    const maxPrice = Math.max(...rates);
    
    if (minPrice === maxPrice) {
      return `$${minPrice.toFixed(2)}`;
    }
    return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <Spinner />
        </div>
      );
    }

    if (error) {
      return (
        <div className="empty-state">
          <svg
            className="empty-state-icon"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="empty-state-title">Error Loading Facilities</h3>
          <p className="empty-state-text">{error}</p>
        </div>
      );
    }

    return (
      <div className="facilities-grid">
        {facilities.map((facility) => (
          <div
            key={facility.id}
            className="facility-card"
            onClick={() => navigate(`/guest/businesses/${facility.businessId}/facilities/${facility.id}`)}
          >
            <div className="facility-image-placeholder">
              <svg
                className="facility-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <div className="facility-content">
              <h2 className="facility-name">{facility.name}</h2>
              <p className="facility-address">{facility.address}</p>
              {facility.description && (
                <p className="facility-description">{facility.description}</p>
              )}
              <div className="facility-footer">
                <div className="facility-price">
                  {formatPrice(facility)}
                </div>
                <button
                  className="view-details-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/guest/businesses/${facility.businessId}/facilities/${facility.id}`);
                  }}
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}

        {facilities.length === 0 && (
          <div className="empty-state">
            <svg
              className="empty-state-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
              />
            </svg>
            <h3 className="empty-state-title">No Courts Available</h3>
            <p className="empty-state-text">
              There are currently no courts available for booking.
              Please check back later.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="guest-page">
      <nav className="guest-nav">
        <div className="nav-content">
          <div className="nav-brand">Courted.ai</div>
        </div>
      </nav>

      <div className="hero-section">
        <h1 className="hero-title">Book Your Court</h1>
        <p className="hero-subtitle">
          Quick and easy court bookings at premier facilities near you.
          No membership required.
        </p>
      </div>

      {renderContent()}
    </div>
  );
}; 